/**
 * useCart — shopping-cart state for online ordering.
 * Persisted to localStorage so it survives navigation/refresh.
 *
 * Supports rich, Square-catalog-aware line items:
 *   {
 *     key,                       // signature: item + variation + modifiers
 *     catalogObjectId,           // Square ITEM id   (null for ad-hoc/legacy)
 *     variationId, variationName,// Square ITEM_VARIATION
 *     name, squareName,
 *     basePrice,                 // variation price (dollars)
 *     modifiers: [{ id, name, priceCents }],
 *     unitPrice,                 // basePrice + modifier prices (dollars)
 *     qty, note
 *   }
 *
 * The legacy `add(menuItem, qty)` path (name + price only) still works.
 *
 * `kiosk` keeps the cart in this tab's sessionStorage under its own key, so a
 * shared ordering tablet never shows (or overwrites) anyone's personal cart.
 */
import { useState, useCallback, useEffect, useMemo } from 'react';

const STORAGE_KEY = 'db_order_cart_v2';
const KIOSK_STORAGE_KEY = 'db_kiosk_cart_v1';

const storeFor = (kiosk) => (kiosk
  ? { area: () => window.sessionStorage, key: KIOSK_STORAGE_KEY }
  : { area: () => window.localStorage, key: STORAGE_KEY });

function load(store) {
  try {
    const raw = store.area().getItem(store.key);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}

function modSig(mods) {
  return (mods || []).map(m => m.id || m.name).sort().join('+');
}

/** Build a stable de-dup key from the chosen item/variation/modifiers. */
function lineKey({ catalogObjectId, name, variationId, modifiers }) {
  return [catalogObjectId || name, variationId || '', modSig(modifiers)].join('|');
}

function unitPriceDollars(basePrice, modifiers) {
  const modCents = (modifiers || []).reduce((s, m) => s + (Number(m.priceCents) || 0), 0);
  return (Number(basePrice) || 0) + modCents / 100;
}

export function useCart({ kiosk = false } = {}) {
  const [items, setItems] = useState(() => load(storeFor(kiosk)));

  useEffect(() => {
    const store = storeFor(kiosk);
    try { store.area().setItem(store.key, JSON.stringify(items)); } catch { /* ignore */ }
  }, [items, kiosk]);

  /** Rich add — used by the configurable item modal. */
  const addConfigured = useCallback((cfg, qty = 1) => {
    const basePrice = Number(cfg.basePrice);
    if (!cfg.name || !Number.isFinite(basePrice) || basePrice < 0) return;
    const modifiers = Array.isArray(cfg.modifiers) ? cfg.modifiers : [];
    const unitPrice = unitPriceDollars(basePrice, modifiers);
    const key = lineKey({ catalogObjectId: cfg.catalogObjectId, name: cfg.name, variationId: cfg.variationId, modifiers });
    const addQty = Math.max(1, Math.min(99, qty));
    setItems(prev => {
      const i = prev.findIndex(x => x.key === key && (x.note || '') === (cfg.note || ''));
      if (i >= 0) {
        const next = [...prev];
        next[i] = { ...next[i], qty: Math.min(99, next[i].qty + addQty) };
        return next;
      }
      return [...prev, {
        key,
        catalogObjectId: cfg.catalogObjectId || null,
        variationId: cfg.variationId || null,
        variationName: cfg.variationName || null,
        name: cfg.name,
        squareName: cfg.squareName || cfg.name,
        basePrice,
        modifiers,
        unitPrice,
        qty: addQty,
        note: cfg.note || '',
      }];
    });
  }, []);

  /** Legacy add — name + price only (no variation/modifiers). */
  const add = useCallback((menuItem, qty = 1) => {
    const name = menuItem?.name;
    const price = Number(menuItem?.price);
    if (!name || !Number.isFinite(price) || price <= 0) return;
    addConfigured({ name, basePrice: price, modifiers: [] }, qty);
  }, [addConfigured]);

  const setQty = useCallback((key, qty) => {
    setItems(prev => {
      const n = Math.max(0, Math.min(99, qty));
      if (n === 0) return prev.filter(x => x.key !== key);
      return prev.map(x => x.key === key ? { ...x, qty: n } : x);
    });
  }, []);

  const inc = useCallback((key) => setItems(prev => prev.map(x => x.key === key ? { ...x, qty: Math.min(99, x.qty + 1) } : x)), []);
  const dec = useCallback((key) => setItems(prev => prev
    .map(x => x.key === key ? { ...x, qty: x.qty - 1 } : x)
    .filter(x => x.qty > 0)), []);

  const setNote = useCallback((key, note) => {
    setItems(prev => prev.map(x => x.key === key ? { ...x, note: (note || '').slice(0, 200) } : x));
  }, []);

  const remove = useCallback((key) => setItems(prev => prev.filter(x => x.key !== key)), []);
  const clear = useCallback(() => setItems([]), []);
  /** Swap in a whole new set of lines (a cart re-priced against the live menu). */
  const replace = useCallback((next) => setItems(Array.isArray(next) ? next : []), []);

  const count = useMemo(() => items.reduce((s, x) => s + x.qty, 0), [items]);
  const subtotal = useMemo(
    () => items.reduce((s, x) => s + x.qty * (Number(x.unitPrice) || Number(x.basePrice) || x.price || 0), 0),
    [items]
  );

  return { items, add, addConfigured, remove, setQty, inc, dec, setNote, clear, replace, count, subtotal };
}
